// A small block-level markdown parser for the export formats. It covers what the
// writer produces (headings, paragraphs, lists, GFM tables, image suggestion
// lines, quotes, bold/italic/links) and treats anything else as a paragraph.

export interface Inline {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; inlines: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'imageSuggestion'; text: string }
  | { kind: 'quote'; text: string };

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const IMAGE_SUGGESTION = /^\s*\[Image suggestion:\s*(.*?)\]\s*$/i;
const QUOTE = /^\s*>\s?(.*)$/;

/** Inline markdown → styled runs. Links keep their text; code keeps its content. */
export function parseInlines(text: string): Inline[] {
  const runs: Inline[] = [];
  const push = (run: Inline) => {
    if (run.text === '') return;
    const last = runs[runs.length - 1];
    if (last && !!last.bold === !!run.bold && !!last.italic === !!run.italic) {
      last.text += run.text;
    } else {
      runs.push({ text: run.text, ...(run.bold ? { bold: true } : {}), ...(run.italic ? { italic: true } : {}) });
    }
  };
  // Links and code first: they may not contain nested styling.
  const stripped = text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]*)`/g, '$1');
  const re = /(\*\*|__)(.+?)\1|(\*|_)(.+?)\3/g;
  let cursor = 0;
  for (const match of stripped.matchAll(re)) {
    const index = match.index ?? 0;
    push({ text: stripped.slice(cursor, index) });
    if (match[2] !== undefined) push({ text: match[2], bold: true });
    else push({ text: match[4], italic: true });
    cursor = index + match[0].length;
  }
  push({ text: stripped.slice(cursor) });
  return runs;
}

export function inlinesToText(inlines: Inline[]): string {
  return inlines.map((r) => r.text).join('');
}

const splitCells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

export function parseMarkdownBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').replace(/\s+/g, ' ').trim();
    paragraph = [];
    if (text) blocks.push({ kind: 'paragraph', inlines: parseInlines(text) });
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      flushParagraph();
      i++;
      continue;
    }
    const heading = line.match(HEADING);
    if (heading) {
      flushParagraph();
      blocks.push({
        kind: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        text: inlinesToText(parseInlines(heading[2].trim())),
      });
      i++;
      continue;
    }
    const image = line.match(IMAGE_SUGGESTION);
    if (image) {
      flushParagraph();
      blocks.push({ kind: 'imageSuggestion', text: image[1].trim() });
      i++;
      continue;
    }
    const quote = line.match(QUOTE);
    if (quote) {
      flushParagraph();
      const parts = [quote[1]];
      while (i + 1 < lines.length && QUOTE.test(lines[i + 1])) {
        parts.push(lines[++i].match(QUOTE)![1]);
      }
      blocks.push({ kind: 'quote', text: inlinesToText(parseInlines(parts.join(' ').trim())) });
      i++;
      continue;
    }
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1])) {
      flushParagraph();
      const header = splitCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        rows.push(splitCells(lines[i]).map((c) => inlinesToText(parseInlines(c))));
        i++;
      }
      blocks.push({ kind: 'table', header: header.map((c) => inlinesToText(parseInlines(c))), rows });
      continue;
    }
    const bullet = line.match(BULLET);
    const numbered = line.match(NUMBERED);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = !!numbered;
      const items: Inline[][] = [];
      while (i < lines.length) {
        const m = ordered ? lines[i].match(NUMBERED) : lines[i].match(BULLET);
        if (!m) break;
        items.push(parseInlines(m[1].trim()));
        i++;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    paragraph.push(line.trim());
    i++;
  }
  flushParagraph();
  return blocks;
}
